/**
 * API Service for Product Module
 *
 * Wrapper around legacy apiService.js for product-related API calls.
 * This allows proper TypeScript typing and mocking in tests.
 *
 * @module product
 * @requirements Sprint 4: FR-011 (API integration for fetch-from-link)
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface TaskPayload {
  description: string;
  audiences: number;
  taskData: {
    id: string;
    name: string;
    description?: string;
    requiredInfo?: string;
    requiredTimeMinutes?: number;
    priority?: string;
    userTrustLevel?: string;
    competitorsForClients?: string;
    userCondition?: string;
  };
}

export interface Progress {
  completed: number;
  total: number;
  currentTask: string;
  success: boolean;
}

export interface TaskContext {
  startTask?: (taskId: string) => void;
}

export interface AudienceResult {
  success: boolean;
  task: TaskPayload;
  error?: string;
}

export interface AudienceResults {
  results: AudienceResult[];
  errors: Array<{ task: TaskPayload; error: string }>;
  successCount: number;
}

export interface ProductData {
  description?: string;
  productUrl?: string;
  platform?: string;
  toneOfVoice?: string;
  designDescription?: string;
  previousUserStepDescription?: string;
  category?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TasksResult = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type QuestionsResult = any;

// ============================================================================
// API WRAPPERS
// ============================================================================

/**
 * Fetch product description from URL
 * This is an async webhook-based API - sends request to backend
 * which processes the URL and updates project via Firestore
 * @requirements Sprint 4: FR-011
 */
export const fetchProductDescriptionFromUrlWithProxy = async (
  productLink: string,
  reviewsLink?: string | null,
  onProgress?: (message: string) => void,
  taskContext?: TaskContext,
  userId?: string,
  projectId?: string
): Promise<{ status: string } | null> => {
  // Dynamic import to handle the legacy JS file path
  // @ts-ignore - legacy JS file without types
  const legacyApiService = await import('../../../../frontend/src/services/apiService');
  const result = await legacyApiService.fetchProductDescriptionFromUrlWithProxy(
    productLink,
    (reviewsLink || null) as any,
    onProgress as any,
    taskContext as any,
    userId,
    projectId
  );
  return result as { status: string } | null;
};

/**
 * Generate audiences for multiple tasks in parallel
 * Used by Create Audiences button
 */
export const generateAudiencesParallelWithProxy = async (
  tasksPayloadArray: TaskPayload[],
  onProgressUpdate?: (progress: Progress) => void,
  taskContext?: TaskContext,
  userId?: string,
  projectId?: string
): Promise<AudienceResults> => {
  // @ts-ignore - legacy JS file without types
  const legacyApiService = await import('../../../../frontend/src/services/apiService');
  return legacyApiService.generateAudiencesParallelWithProxy(
    tasksPayloadArray,
    onProgressUpdate as any,
    taskContext as any,
    userId,
    projectId
  );
};

/**
 * Generate audiences via new AI agent endpoint (replaces legacy webhook).
 * Fire-and-forget: returns taskId. Frontend tracks via Firestore listener.
 * Supports standard AI mode and Deep Research (Gemini Interactions API).
 */
export const generateAudiencesViaAgent = async (
  projectId: string,
  userId: string,
  selectedTaskIds: string[],
  audienceCount: number,
  options?: { useDeepResearch?: boolean; provider?: string; model?: string },
): Promise<{ taskId: string }> => {
  const { AgentApiClient } = await import('@modules/content-agent/services/AgentApiClient');
  const client = new AgentApiClient();
  const response = await client.generateAudiences({
    projectId,
    userId,
    selectedTaskIds,
    audienceCount,
    useDeepResearch: options?.useDeepResearch,
    provider: options?.provider as any,
    model: options?.model,
  });
  if (!response.success) {
    throw new Error(response.error || 'Failed to start audience generation');
  }
  return { taskId: response.taskId! };
};

/**
 * Generate tasks from product description
 * Used by Create Tasks from Description button
 */
export const generateTasksFromDescription = async (
  productData: ProductData,
  onProgress?: (progress: Progress) => void,
  taskContext?: TaskContext,
  userId?: string,
  projectId?: string
): Promise<TasksResult> => {
  // @ts-ignore - legacy JS file without types
  const legacyApiService = await import('../../../../frontend/src/services/apiService');
  return legacyApiService.generateTasksFromDescription(
    productData,
    onProgress as any,
    taskContext as any,
    userId,
    projectId
  );
};

/**
 * Generate interview questions with all product fields
 * Used by Update Interview Questions button
 */
export const generateQuestionsWithAllProductFields = async (
  productData: ProductData,
  onProgress?: (progress: Progress) => void,
  taskContext?: TaskContext,
  userId?: string,
  projectId?: string
): Promise<QuestionsResult> => {
  // @ts-ignore - legacy JS file without types
  const legacyApiService = await import('../../../../frontend/src/services/apiService');
  return legacyApiService.generateQuestionsWithAllProductFields(
    productData,
    onProgress as any,
    taskContext as any,
    userId,
    projectId
  );
};
